import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import {
  PiCheck,
  PiNavigationArrow,
  PiPlus,
  PiTrash,
} from 'react-icons/pi';
import { useGetLabelLightList } from 'services/labels/light';
import { IEventForm } from '../EventForm';
import { IEventLabel } from 'services/events/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

const EventLabels = () => {
  const {
    values,
    status,
    setFieldValue,
  }: IFormikContextType<IEventForm> =
    useFormikContext<IEventForm>();
  const { t } = useTranslation();
  const navigateToNewWindow = useNewWindowNavigate();

  const labels = useGetLabelLightList();

  const { applicationSettings } = useStoreState();

  const getLabelOptions = (currentLabels: IEventLabel[]) =>
    labels.data
      ?.filter(label =>
        applicationSettings?.allowContractsWithSameLabel
          ? true
          : !currentLabels.map(labelItem => labelItem.labelId).includes(label.id)
      )
      .map(label => ({ value: label.id, label: label.labelName })) ?? [];

  const nodes = values.labels.map((label, index) => ({
    ...label,
    id: index,
  }));

  const getDivisionPercentageSum = (labels: IEventLabel[]) =>
    labels
      .map(label => label.divisionPercentage ?? 0)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0) ?? 0;

  const getLabelsWithoutDivision = (labels: IEventLabel[]) =>
    labels.filter(
      ({ divisionPercentage }) =>
        divisionPercentage === null || divisionPercentage === undefined
    ).length ?? 1;

  const getPercentageLeft = (labels: IEventLabel[]) =>
    getDivisionPercentageSum(labels) < 100
      ? parseFloat(
          (
            (100 - getDivisionPercentageSum(labels)) /
            getLabelsWithoutDivision(labels)
          ).toFixed(2)
        )
      : 0;

  const onAddLabel = () => {
    setFieldValue(
      `labels[${values.labels.length}]`,
      {
        labelId: '',
        divisionPercentage: null,
      }
    );
  };

  const onRemoveLabel = (index: number) => {
    setFieldValue(
      'labels',
      values.labels.filter((_, key) => key !== index)
    );
  };

  const getAllLabelsSelected = (labels: IEventLabel[]) =>
    !!labels.length
      ? !!labels[labels.length - 1].labelId.length
      : true;

  return (
    <TopbarPage
      topbar={
        <div
        />
      }>
      <div className='flex w-full'>
        <Table
          id={'EventLabels'}
          size='compact'
          data={{ nodes: nodes }}
          columns={[
            {
              label: `${t('input.label.global')}`,
              renderCell: ({ labelId: id, id: index }) => {
                const labelName = labels.data?.find(label => label.id === id)?.labelName || '';
                return (
                  <TableItem>
                    {status?.displayMode ? (
                      <TableItem.Text text={labelName} />
                    ) : (
                      <FormInput.Select
                        name={`labels[${index}].labelId`}
                        isAsync
                        isSearchable
                        label={t(`input.label.label`)}
                        value={{
                          label: labelName,
                          value: id,
                        }}
                        options={getLabelOptions(values.labels)}
                        isLoading={labels.isLoading}
                      />
                    )}
                  </TableItem>
                );
              },
              resize: true,
            },
            {
              label: t(`input.label.division`),
              renderCell: ({ divisionPercentage, id: index }) => (
                <TableItem>
                  {status?.displayMode ? (
                    <TableItem.Text text={`${divisionPercentage ?? '-'}%`} />
                  ) : (
                    <div className='flex w-full gap-2'>
                      <div>
                        <FormInput.Number
                          name={`labels[${index}].divisionPercentage`}
                          label={t(`input.label.division`)}
                          suffix=' %'
                          placeholder={`${getPercentageLeft(values.labels)} %`}
                        />
                      </div>
                      {values.labels[index].divisionPercentage === null && (
                        <Button
                          size='compact'
                          isClear
                          onlyIcon
                          onClick={() =>
                            setFieldValue(
                              `labels[${index}].divisionPercentage`,
                              getPercentageLeft(values.labels)
                            )
                          }>
                                                    <Button.Description description={t('button.apply')} />

                                                    <Button.Icon icon={<PiCheck />} />
                        </Button>
                      )}
                    </div>
                  )}
                </TableItem>
              ),
            },
            {
              label: '',
              renderCell: ({ id: index, labelId }) => (
                <TableItem justifyPosition='end'>
                  {status?.displayMode ? (
                    <Button
                      isClear
                      onlyIcon
                      onClick={() =>
                        navigateToNewWindow(`label/${labelId}/finances`)
                      }>
                                            <Button.Description description={t('button.open')} />

                                            <Button.Icon
                        icon={<PiNavigationArrow transform='scale(-1,1)' />}
                      />
                    </Button>
                  ) : (
                    <Button
                      variant={BUTTON_VARIANT.remove}
                      isClear
                      onlyIcon
                      onClick={() => onRemoveLabel(index)}>
                                            <Button.Description description={t('button.remove')} />

                                            <Button.Icon icon={<PiTrash />} />
                    </Button>
                  )}
                </TableItem>
              ),
            },
          ]}
          tableOptions={
            status?.displayMode
              ? undefined
              : {
                  renderAfterTable: () => (
                    <tfoot className='full-width-footer'>
                      <TableMessage
                        className='cursor-pointer'
                        disable={!getAllLabelsSelected(values.labels)}
                        onClick={onAddLabel}>
                        <TableItem.Text text={t('button.add')} />
                        <PiPlus />
                      </TableMessage>
                    </tfoot>
                  ),
                }
          }
        />
      </div>
    </TopbarPage>
  );
};

export default EventLabels;
